<template>
  <aw-drag
    :enable="bcDocumentStore.edit && parent.data.sort === 'rank' && !element.file?.deleted"
    :drag-item="element"
    #default="{ dragHandle }"
    :drag-start-timeout="200"
    class="relative"
    :class="{ 
      'bg-checkered-lg': blackBackground,
      'bg-white': !blackBackground,
      DocumentHighlightSearch: $route.hash === '#el' + element.id,
      'cursor-pointer': canShowInGallery
    }"
    style="min-height: 150px"
    :id="'el' + element.id"
    @mouseenter="showActions = true"
    @mouseleave="showActions = false"
  >
    <tippy 
      v-if="file?.deleted"
      :content="$t('File was deleted')" 
      to="parent"
    />
    <tippy 
      v-else-if="file_upload?.id"
      :content="$t('Waiting for download file from external storage')" 
      to="parent"
    />
    <tippy 
      v-else-if="antivirusState === 'hasVirus'"
      :content="$t('File has virus')" 
      theme='warning'
      to="parent"
    />
    <div
      class="GalleryList__More"
      v-if="index == 7 && !expanded && !edit && diff > 0"
      @click.stop.prevent="expand()"
    >
      <span>+{{ diff + 1 }}</span>
    </div>

    <div
      v-else-if="antivirusState === 'hasVirus'"
      class="flex items-center justify-center absolute inset-0 pointer-events-none select-none"
    >
      <aw-icon 
        icon="bug-report" 
        class="h-24 w-24 text-red-600"
      />
    </div>

    <div
      v-else-if="file?.deleted"
      class="flex items-center justify-center absolute inset-0 pointer-events-none select-none"
    >
      <aw-icon 
        icon="trash-slim" 
        class="h-24 w-24 text-gray-300"
      />
    </div>

    <div
      v-else-if="antivirusState === 'waiting' || !hasPreview"
      class="flex items-center justify-center text-center absolute inset-0 pointer-events-none select-none"  
    >
      <div class="flex-grow">
        <file-icon :ext="fileExt" size="large" />
      </div>
    </div>

    <img 
      v-else-if="hasPreview"
      v-show="imagePreviewLoaded"
      :src="imagePreviewLoaded ? element.file?.previews['500x500'] : undefined" 
      alt="" 
      class="pointer-events-none select-none"
      ref="imagePreview"
      loading="lazy"
    />
    
    <div
      v-else-if="!imagePreviewLoaded"
      class="flex items-center justify-center absolute inset-0 pointer-events-none select-none"
    >
      <aw-icon icon="loader" class="h-24 w-24 text-bc-blue " />
    </div>
    
    <div class="GalleryList__Actions">
      <span 
        class="GalleryList__ActionsVirusState mr-2.5"
        v-if="showActions"
      >
        <aw-icon
          icon="info"
          class="w-5 h-5 text-gray-400 cursor-pointer"
        />
        <document-element-file-metadata-tippy
          :antivirus-state="antivirusState"
          :element="element"
        />
      </span>
      <span
        ref="DocumentCircle"
        class="DocumentCircle mr-2.5 sm:mr-3.5"
        :class="{
          'DocumentCircle--active': bcSelectedStore.selected.has(element.id)
        }"
        :data-item-index="element.root_rank"
        @click.prevent.stop="bcSelectedStore.select(
          element, 
          {
            isRecursive: true,
            isShift: $event.shiftKey,
            isDocument: true,
            itemIndex: element.root_rank,
          }
        )"
      ></span>
      <span class="cursor-move text-gray-400 hover:text-gray-500" v-if="edit && parent.data.sort === 'rank' && !element.file?.deleted" :ref="dragHandle">
        <aw-icon icon="dots" class="w-4 h-4 -mx-1 "></aw-icon>
        <aw-icon icon="dots" class="w-4 h-4 -mx-1 "></aw-icon>
      </span>
    </div>

    <div
      @click.stop="" 
      class="GalleryList__Download text-gray-400 hover:text-gray-500 z-5 flex justify-between items-center"
    >
      <contenteditable
        v-if="edit && !file?.deleted"
        tag="div"
        v-model="fileName"
        :contenteditable="edit"
        :noNL="true"
        :noHTML="true"
        :alwaysSelectAll="element.file?.name"
        class="GalleryList__DownloadName text-gray-800"
        @updated="updated = 'name', blurNameInput()"
        @blur="blurNameInput"
        @focused="focusNameInput"
      />
      <a
        v-else-if="hasPreview && antivirusState !== 'hasVirus' && !file?.deleted"
        class="GalleryList__DownloadName cursor-pointer text-gray-800"
        href="#"
        @click.stop.prevent="openGallery(parent.id, element.file ? element.file : element.file_upload)"
      >
        {{ fileName }}
      </a>
      <a
        v-else-if="data.can_download && isDownloadable"
        class="GalleryList__DownloadName cursor-pointer text-gray-800"
        :href="file?.download_url.original"
        @click.stop.prevent="downloadFile"
      >
        {{ fileName }}
      </a>
      <div 
        v-else
        class="GalleryList__DownloadName"
        :class="antivirusState === 'hasVirus' ? 'text-red-600' : file?.deleted || file_upload?.id ? 'text-gray-300' : 'text-gray-800'"
      >
        {{ fileName }}
      </div>
      <div 
        v-if="data.can_download && isDownloadable"
        class="GalleryList__DownloadIcon cursor-pointer" 
      >
        <document-element-file-download-link 
          :gallery="true" 
          :is-pill="true" 
          :file="element.file" 
        />
      </div>
    </div>
  </aw-drag>
</template>

<script>
import FileState from '../../mixins/FileState'
import FileElement from '../../mixins/FileElement'
import Gallery from '../../mixins/Gallery'
import ImagePreview from '../../mixins/ImagePreview'
import Selected from '../../mixins/Selected'

export default {
  emits: ['expand-from-child'],
  mixins: [
    FileState,
    FileElement,
    Gallery,
    ImagePreview,
    Selected,
  ],
  props: ['index', 'expanded', 'diff'],
  dragDirection: 'horizontal',
  data() {
    return {
      showActions: false
    }
  },
  methods: {
    expand() {
      this.$emit('expand-from-child')
    },
  },
  mounted() {
    this.assignItemToSelectBtn('DocumentCircle', this.element)
  },
  watch: {
    'element.file.previews': {
      async handler(val) {
        if (val && val['500x500']) {
          await this.doLoadImagePreview('500x500')
        }
      },
      immediate: true,
    }
  }
}
</script>

<style lang="scss" scoped></style>
